import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  VStack,
  useToast,
  Grid,
  GridItem,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Switch,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';

// Components
import SummaryStats from './components/SummaryStats';
import RunProgressSection from './components/RunProgressSection';
import ActionButtons from './components/ActionButtons';
import FilterSection from './components/FilterSection';
import RepricerTable from './components/RepricerTable';
import Pagination from './components/Pagination';
import ScheduleSettingsModal from './components/ScheduleSettingsModal';
import RulesSettingsModal from './components/RulesSettingsModal';
import CompetitorSettingsModal from './components/CompetitorSettingsModal';
import StartProcessModal from './components/StartProcessModal';

// API imports
import { getMarketplacesListApi } from 'features/marketplaces/marketplaces.api';
import { getStoresListApi } from 'features/stores/stores.api';
import { getSuppliersListApi } from 'features/suppliers/suppliers.api';
import {
  getRepricerSettingsApi,
  updateRepricerSettingsApi,
  runRepricerApi,
  resetCompletedListingsApi,
  getStoreRepricerSettingsApi,
  updateStoreRepricerSettingsApi,
  getRepricerListingsApi,
  updateListingRepricerApi,
  getRepricerStatusCountsApi,
  getListingRepricerDetailsApi,
  unlinkListingCompetitorApi,
  bulkUpdateListingsApi,
  getRepricerProcessStatusApi,
} from 'features/listing-repricer/listing-repricer.api';

const ListingRepricer = () => {
  const dispatch = useDispatch();
  const toast = useToast();

  // Redux selectors
  const { list: marketplaceOptions, getMarketplacesListStatus } = useSelector(
    (state) => state.marketplaces,
  );
  const { list: storeOptions, getStoresListStatus } = useSelector(
    (state) => state.stores,
  );
  const { list: supplierOptions, getSuppliersListStatus } = useSelector(
    (state) => state.suppliers,
  );
  const {
    settings: repricerSettings,
    isRepricerRunning,
    runRepricerStatus,
    storeRepricerSettings,
    getStoreRepricerSettingsStatus,
    listings,
    listingsCurrentPage,
    listingsTotalPages,
    listingsTotalCount,
    getRepricerListingsStatus,
    statusCounts,
    getRepricerStatusCountsStatus,
    selectedListingDetails,
    getListingRepricerDetailsStatus,
    bulkUpdateStatus,
    bulkUpdateResult,
    processStatus,
  } = useSelector((state) => state.listingRepricer);

  // Debug logging to see Redux state
  console.log('Listing Repricer - Redux state:', {
    listings,
    listingsCurrentPage,
    listingsTotalPages,
    listingsTotalCount,
    getRepricerListingsStatus,
  });

  // Local state
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchField, setSearchField] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [, setActualSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('dateCreated+');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    priceMin: '',
    priceMax: '',
    competitorPriceMin: '',
    competitorPriceMax: '',
    marketplace: '',
    supplier: '',
    store: '',
    status: '',
  });

  // Modal states
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showCompetitorModal, setShowCompetitorModal] = useState(false);
  const [showStartProcessModal, setShowStartProcessModal] = useState(false);
  const [showAutoSelectPopup, setShowAutoSelectPopup] = useState(false);
  const [autoSelectEnabled, setAutoSelectEnabled] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // Local state for pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pageInput, setPageInput] = useState(1);

  const loadOptimizationListings = useCallback(() => {
    const apiFilters = {
      status: filters.status || undefined,
      storeId: filters.store || undefined,
      searchTerm: searchQuery || undefined,
      searchField: searchField || undefined,
      priceMin: filters.priceMin || undefined,
      priceMax: filters.priceMax || undefined,
      competitorPriceMin: filters.competitorPriceMin || undefined,
      competitorPriceMax: filters.competitorPriceMax || undefined,
      marketplace: filters.marketplace || undefined,
      supplier: filters.supplier || undefined,
      store: filters.store || undefined,
    };

    console.log('Loading optimization listings with filters:', apiFilters);
    console.log('Page:', currentPage, 'PageSize:', pageSize);

    dispatch(
      getRepricerListingsApi({
        page: currentPage,
        limit: pageSize,
        filters: Object.keys(apiFilters).some((key) => apiFilters[key] !== undefined)
          ? apiFilters
          : undefined,
      }),
    );
  }, [dispatch, filters, searchQuery, searchField, currentPage, pageSize]);

  // Load initial data
  useEffect(() => {
    dispatch(getMarketplacesListApi());
    dispatch(getStoresListApi());
    dispatch(getSuppliersListApi());
    dispatch(getRepricerSettingsApi());
    dispatch(getRepricerStatusCountsApi());
    dispatch(getRepricerProcessStatusApi());
    loadOptimizationListings();
  }, [dispatch, loadOptimizationListings]);

  // Load optimization listings when filters change
  useEffect(() => {
    loadOptimizationListings();
  }, [loadOptimizationListings]);

  // Sync page input with current page
  useEffect(() => {
    setPageInput(listingsCurrentPage || currentPage);
  }, [listingsCurrentPage, currentPage]);

  // Load store optimizer settings when store is selected
  useEffect(() => {
    if (selectedStoreId) {
      dispatch(getStoreRepricerSettingsApi(selectedStoreId));
    }
  }, [dispatch, selectedStoreId]);

  const handleFilterChange = (field, value) => {
    if (field === 'clear') {
      setFilters(value);
    } else {
      setFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSearch = () => {
    setActualSearchTerm(searchQuery);
    // Trigger search with current filters
    loadOptimizationListings();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPageInput(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    setPageInput(1);
  };

  const handleScheduleSettings = () => {
    setShowScheduleModal(true);
  };

  const handleRulesSettings = () => {
    setShowRulesModal(true);
  };

  const handleCompetitorSettings = () => {
    setShowCompetitorModal(true);
  };

  const handleStartProcess = () => {
    setShowStartProcessModal(true);
  };

  const handleConfirmStartProcess = async (maxListings, supplierId) => {
    try {
      const result = await dispatch(
        runRepricerApi({
          maxListings: maxListings,
          storeId: selectedStoreId,
          supplierId: supplierId || undefined,
        }),
      );

      if (runRepricerApi.fulfilled.match(result)) {
        toast({
          title: 'Optimization Process Started',
          description: `The optimization process is now running for ${maxListings.toLocaleString()} listings.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setShowStartProcessModal(false);
      } else {
        toast({
          title: 'Failed to Start Optimization',
          description: result.payload?.error || 'Unknown error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start optimization process',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleStopProcess = async () => {
    try {
      await dispatch(resetCompletedListingsApi());
      toast({
        title: 'Optimization Process Stopped',
        description:
          'The optimization process has been stopped and completed listings reset.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to stop optimization process',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAcceptOptimization = async (listingId, updateData) => {
    try {
      await dispatch(updateListingRepricerApi({ listingId, updateData })).unwrap();

      toast({
        title: 'Optimization Accepted',
        description: `Listing optimization saved with price $${updateData.suggestedPrice?.toFixed(2) || 'N/A'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the listings to show updated status
      loadOptimizationListings();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save optimization. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAddToQueue = async (listingId, updateData) => {
    try {
      await dispatch(updateListingRepricerApi({ listingId, updateData })).unwrap();

      toast({
        title: 'Added to Queue',
        description: `Listing added to queue with price $${updateData.suggestedPrice?.toFixed(2) || 'N/A'}`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the listings to show updated status
      loadOptimizationListings();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add to queue',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handlePutOnHold = async (listingId, updateData) => {
    try {
      await dispatch(updateListingRepricerApi({ listingId, updateData })).unwrap();

      toast({
        title: 'Put on Hold',
        description: 'Listing has been put on hold',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the listings to show updated status
      loadOptimizationListings();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to put on hold',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUnhold = async (listingId, updateData) => {
    try {
      await dispatch(updateListingRepricerApi({ listingId, updateData })).unwrap();

      toast({
        title: 'Unheld',
        description: 'Listing has been unheld and returned to optimized status',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the listings to show updated status
      loadOptimizationListings();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to unhold',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUnlinkCompetitor = async (listingId) => {
    try {
      await dispatch(unlinkListingCompetitorApi(listingId)).unwrap();

      toast({
        title: 'Competitor Unlinked',
        description:
          'Listing has been unlinked from competitor and returned to optimized status',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the listings to show updated status
      loadOptimizationListings();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to unlink competitor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleBulkUpdate = async (action) => {
    if (selectedItems.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select at least one listing',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (isBulkUpdating) return; // Prevent multiple simultaneous operations

    setIsBulkUpdating(true);
    try {
      const result = await dispatch(
        bulkUpdateListingsApi({
          listingIds: selectedItems,
          action,
        }),
      ).unwrap();

      const actionNames = {
        'put-on-hold': 'Put on Hold',
        'unlink-competitors': 'Unlink Competitors',
        'make-active': 'Make Active',
        'make-unhold': 'Make Unhold',
      };

      toast({
        title: 'Bulk Update Complete',
        description: `${result.updatedCount} listings ${actionNames[action]?.toLowerCase() || 'updated'} successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Clear selection and refresh listings
      setSelectedItems([]);
      loadOptimizationListings();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update listings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleRejectOptimization = (listingId) => {
    toast({
      title: 'Price Rejected',
      description: 'The optimized price has been rejected.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
    // TODO: Update listing status via API call
    // For now, the status will be updated when the data is refetched
  };

  const handleLoadListingDetails = async (listingId) => {
    try {
      await dispatch(getListingRepricerDetailsApi(listingId)).unwrap();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load listing details. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSaveSchedule = async (scheduleData) => {
    try {
      const payload = { ...repricerSettings, ...scheduleData };
      const result = await dispatch(updateRepricerSettingsApi(payload));

      if (updateRepricerSettingsApi.fulfilled.match(result)) {
        toast({
          title: 'Schedule Settings Saved',
          description: 'Your optimization schedule has been updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setShowScheduleModal(false);
      } else {
        toast({
          title: 'Failed to Save Schedule',
          description: result.payload?.error || 'Unknown error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save schedule settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSaveRules = async (rulesData) => {
    try {
      if (selectedStoreId) {
        const result = await dispatch(
          updateStoreRepricerSettingsApi({
            storeId: selectedStoreId,
            updateData: rulesData,
          }),
        );

        if (updateStoreRepricerSettingsApi.fulfilled.match(result)) {
          toast({
            title: 'Pricing Rules Saved',
            description: 'Your pricing rules have been updated successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          setShowRulesModal(false);
        } else {
          toast({
            title: 'Failed to Save Rules',
            description: result.payload?.error || 'Unknown error occurred',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: 'No Store Selected',
          description: 'Please select a store before saving pricing rules.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save pricing rules',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (showAutoSelectPopup && repricerSettings) {
      setAutoSelectEnabled(Boolean(repricerSettings.autoCompetitorEnabled));
    }
  }, [showAutoSelectPopup, repricerSettings?.autoCompetitorEnabled]);

  const handleSaveAutoSelect = async () => {
    try {
      const payload = { ...repricerSettings, autoCompetitorEnabled: autoSelectEnabled };
      const result = await dispatch(updateRepricerSettingsApi(payload));

      if (updateRepricerSettingsApi.fulfilled.match(result)) {
        toast({
          title: 'Auto-select saved',
          description: 'Auto-select competitor setting has been updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setShowAutoSelectPopup(false);
      } else {
        toast({
          title: 'Failed to save',
          description: result.payload?.error || 'Unknown error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save auto-select setting',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSaveCompetitorSettings = async (competitorData) => {
    try {
      const payload = { ...repricerSettings, ...competitorData };
      const result = await dispatch(updateRepricerSettingsApi(payload));

      if (updateRepricerSettingsApi.fulfilled.match(result)) {
        toast({
          title: 'Competitor Settings Saved',
          description: 'Your competitor settings have been updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setShowCompetitorModal(false);
      } else {
        toast({
          title: 'Failed to Save Competitor Settings',
          description: result.payload?.error || 'Unknown error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save competitor settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={6} mt="50px">
      <VStack spacing={8} align="stretch">
        {/* Header Section */}
        <Box>
          <VStack spacing={3} align="stretch">
            {/* Status Cards */}
            <SummaryStats
              statusCounts={statusCounts}
              isLoading={getRepricerStatusCountsStatus === 'pending'}
            />

            {/* Progress bars and last run */}
            <RunProgressSection
              processStatus={processStatus}
              onRefetchProcessStatus={() => dispatch(getRepricerProcessStatusApi())}
            />

            {/* Action Buttons */}
            <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={3}>
              <GridItem>
                <ActionButtons
                  onScheduleSettings={handleScheduleSettings}
                  onRulesSettings={handleRulesSettings}
                  onStartProcess={handleStartProcess}
                  onStopProcess={handleStopProcess}
                  onCompetitorSettings={handleCompetitorSettings}
                  isProcessRunning={isRepricerRunning}
                />
              </GridItem>
              <GridItem>
                {/* Ready for Review Card */}
                <Box
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  boxShadow="sm"
                  borderWidth="1px"
                  borderColor="gray.200"
                  width="100%"
                  height="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="stretch"
                >
                  <Flex justify="space-between" align="flex-start" width="100%" gap={3}>
                    <Box flex={1}>
                      <Box fontSize="sm" color="gray.600" fontWeight="500" mb={1}>
                        Ready for Review
                      </Box>
                      <Box fontSize="2xl" fontWeight="bold" color="gray.800">
                        {statusCounts?.readyForReview || 0}
                      </Box>
                      <Box fontSize="xs" color="gray.500" mt={1}>
                        Awaiting review
                      </Box>
                      <Box fontSize="xs" color="gray.600" mt={2}>
                        Auto-select:{' '}
                        <Text as="span" fontWeight="600" color={repricerSettings?.autoCompetitorEnabled ? 'green.600' : 'gray.500'}>
                          {repricerSettings?.autoCompetitorEnabled ? 'On' : 'Off'}
                        </Text>
                      </Box>
                    </Box>
                    <Flex align="center" gap={2} flexShrink={0}>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                        onClick={() => setShowAutoSelectPopup(true)}
                        fontWeight="500"
                      >
                        Auto-select
                      </Button>
                      <Box
                        w={8}
                        h={8}
                        bg="purple.100"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Box w={4} h={4} bg="purple.500" borderRadius="full" />
                      </Box>
                    </Flex>
                  </Flex>
                </Box>
              </GridItem>
            </Grid>
          </VStack>
        </Box>

        {/* Filter Section */}
        <FilterSection
          searchField={searchField}
          setSearchField={setSearchField}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          filters={filters}
          handleFilterChange={handleFilterChange}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          marketplaceOptions={marketplaceOptions}
          storeOptions={storeOptions}
          supplierOptions={supplierOptions}
          getMarketplacesListStatus={getMarketplacesListStatus}
          getStoresListStatus={getStoresListStatus}
          getSuppliersListStatus={getSuppliersListStatus}
          pageSize={pageSize}
          handlePageSizeChange={handlePageSizeChange}
          currentPage={listingsCurrentPage || currentPage}
          totalListings={listingsTotalCount || 0}
          listings={listings}
        />

        {/* Optimizer Table */}
        <RepricerTable
          listings={listings}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          sortBy={sortBy}
          setSortBy={setSortBy}
          isLoading={getRepricerListingsStatus === 'pending'}
          onAcceptOptimization={handleAcceptOptimization}
          onAddToQueue={handleAddToQueue}
          onPutOnHold={handlePutOnHold}
          onUnhold={handleUnhold}
          onUnlinkCompetitor={handleUnlinkCompetitor}
          onBulkUpdate={handleBulkUpdate}
          onRejectOptimization={handleRejectOptimization}
          storeRules={storeRepricerSettings}
          isBulkUpdating={isBulkUpdating}
          onPageChange={handlePageChange}
          currentPage={listingsCurrentPage || currentPage}
          totalPages={listingsTotalPages || 1}
          priorityCompetitors={repricerSettings?.priorityCompetitors || []}
          onLoadListingDetails={handleLoadListingDetails}
          selectedListingDetails={selectedListingDetails}
          getListingRepricerDetailsStatus={getListingRepricerDetailsStatus}
        />

        {/* Pagination */}
        <Pagination
          currentPage={listingsCurrentPage || currentPage}
          totalPages={listingsTotalPages || 1}
          pageInput={pageInput}
          setPageInput={setPageInput}
          handlePageChange={handlePageChange}
          isLoading={getRepricerListingsStatus === 'pending'}
          listings={listings}
        />
      </VStack>

      {/* Modals */}
      <ScheduleSettingsModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSave={handleSaveSchedule}
        currentSettings={repricerSettings}
        storeOptions={storeOptions}
      />

      <RulesSettingsModal
        isOpen={showRulesModal}
        onClose={() => setShowRulesModal(false)}
        onSave={handleSaveRules}
        currentSettings={storeRepricerSettings}
        storeOptions={storeOptions}
        selectedStoreId={selectedStoreId}
        onStoreChange={setSelectedStoreId}
        isLoading={getStoreRepricerSettingsStatus === 'pending'}
      />

      <CompetitorSettingsModal
        isOpen={showCompetitorModal}
        onClose={() => setShowCompetitorModal(false)}
        onSave={handleSaveCompetitorSettings}
        currentSettings={repricerSettings}
      />

      <StartProcessModal
        isOpen={showStartProcessModal}
        onClose={() => setShowStartProcessModal(false)}
        onConfirm={handleConfirmStartProcess}
        currentSettings={repricerSettings}
        storeOptions={storeOptions}
        selectedStoreId={selectedStoreId}
        onStoreChange={setSelectedStoreId}
        supplierOptions={supplierOptions}
        selectedSupplierId={selectedSupplierId}
        onSupplierChange={setSelectedSupplierId}
        isLoading={runRepricerStatus === 'pending'}
      />

      {/* Auto-select competitor popup (Ready for Review) */}
      <Modal isOpen={showAutoSelectPopup} onClose={() => setShowAutoSelectPopup(false)} size="md">
        <ModalOverlay />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader color="gray.800" fontSize="lg" fontWeight="600">
            Auto-select competitor
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text fontSize="sm" color="gray.600" mb={4}>
              After a fetch (manual or schedule), automatically pick the best competitor from
              priority sellers and set listings to active when they pass validation. Only single-part
              listings are considered; sets and kits are skipped.
            </Text>
            <FormControl display="flex" alignItems="center">
              <FormLabel color="gray.700" fontSize="sm" fontWeight="500" mb={0} mr={3}>
                Auto-select competitor
              </FormLabel>
              <Switch
                isChecked={autoSelectEnabled}
                onChange={(e) => setAutoSelectEnabled(e.target.checked)}
                colorScheme="purple"
                size="md"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter pt={4} borderTop="1px solid" borderColor="gray.100">
            <Button variant="ghost" mr={3} onClick={() => setShowAutoSelectPopup(false)} fontWeight="500">
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSaveAutoSelect}
              borderRadius="md"
              fontWeight="500"
              px={6}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ListingRepricer;
